import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Star, ChevronLeft, ChevronRight, Quote, Pause, Play } from 'lucide-react';
import { Button } from './ui/button';
import { testimonials } from '../data/mockData';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  // Auto-slide effect
  useEffect(() => {
    if (!isPaused) {
      const interval = setInterval(() => {
        nextTestimonial();
      }, 5000); // Change slide every 5 seconds

      return () => clearInterval(interval);
    }
  }, [currentIndex, isPaused]);

  return (
    <section className="py-20 bg-gradient-to-br from-[#FEF5F5] via-white to-[#FDFEFE] relative overflow-hidden">
      {/* Enhanced Decorative elements */}
      <div className="absolute top-10 right-10 w-96 h-96 bg-[#C0392B]/5 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-10 left-10 w-96 h-96 bg-[#7F8C8D]/5 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}} />
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-[#C0392B]/3 to-[#7F8C8D]/3 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4 max-w-[1320px] relative z-10">
        {/* Section Header with Badge */}
        <div className="text-center mb-16 space-y-6 animate-in fade-in slide-in-from-top duration-700">
          <div className="inline-block">
            <span className="bg-[#C0392B]/10 text-[#C0392B] px-4 py-2 rounded-full text-sm font-semibold">
              Client Success Stories
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#2C3E50]">
            What Our Clients Say
          </h2>
          <p className="text-xl text-[#2C3E50] max-w-3xl mx-auto leading-relaxed">
            Don't just take our word for it - hear from businesses we've helped protect their brands
          </p>
        </div>

        {/* Testimonials Carousel */}
        <div className="max-w-5xl mx-auto">
          <div 
            className="relative"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Main Testimonial Card - Enhanced Design */}
            <Card className="border-2 border-[#7F8C8D]/20 shadow-2xl bg-gradient-to-br from-white to-[#FDFEFE] hover:shadow-3xl transition-all duration-500 overflow-hidden">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, #C0392B 1px, transparent 1px)', backgroundSize: '30px 30px'}} />
              </div>
              
              <CardContent className="relative p-8 md:p-12">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
                  {/* Left: Image and Info */}
                  <div className="flex flex-col items-center space-y-4">
                    {/* Profile Image with Decorative Ring */}
                    <div className="relative">
                      <div className="absolute inset-0 bg-gradient-to-br from-[#C0392B] to-[#A93226] rounded-full blur-xl opacity-20 animate-pulse" />
                      <div className="relative w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-xl ring-4 ring-[#C0392B]/20">
                        <img
                          src={testimonials[currentIndex].image}
                          alt={testimonials[currentIndex].name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      {/* Quote Badge */}
                      <div className="absolute -bottom-2 -right-2 w-12 h-12 bg-[#C0392B] rounded-full flex items-center justify-center shadow-lg">
                        <Quote className="w-6 h-6 text-white" />
                      </div>
                    </div>

                    {/* Author Info */}
                    <div className="text-center">
                      <p className="text-xl font-bold text-[#2C3E50]">
                        {testimonials[currentIndex].name}
                      </p>
                      <p className="text-[#2C3E50] font-medium">
                        {testimonials[currentIndex].company}
                      </p>
                      <div className="inline-block mt-2 px-3 py-1 bg-[#C0392B]/10 rounded-full">
                        <p className="text-sm text-[#C0392B] font-semibold">
                          {testimonials[currentIndex].service}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Right: Testimonial Content */}
                  <div className="md:col-span-2 space-y-6">
                    {/* Testimonial Text */}
                    <div className="relative">
                      <p className="text-xl text-[#2C3E50] leading-relaxed italic relative z-10 text-center md:text-left">
                        "{testimonials[currentIndex].text}"
                      </p>
                    </div>

                    {/* Rating */}
                    <div className="flex justify-center md:justify-start space-x-1">
                      {[...Array(Math.floor(testimonials[currentIndex].rating))].map((_, i) => (
                        <Star key={i} className="w-5 h-5 fill-[#C0392B] text-[#C0392B]" />
                      ))}
                      {testimonials[currentIndex].rating % 1 !== 0 && (
                        <div className="relative w-5 h-5">
                          <Star className="w-5 h-5 text-[#C0392B]" />
                          <div className="absolute inset-0 overflow-hidden" style={{width: '50%'}}>
                            <Star className="w-5 h-5 fill-[#C0392B] text-[#C0392B]" />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Enhanced Navigation Controls */}
            <div className="flex justify-center items-center space-x-6 mt-12">
              <Button
                onClick={prevTestimonial}
                variant="outline"
                size="icon"
                className="w-14 h-14 rounded-full border-2 border-[#C0392B] text-[#C0392B] hover:bg-[#C0392B] hover:text-white transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl"
              >
                <ChevronLeft className="w-6 h-6" />
              </Button>

              {/* Dots Indicator */}
              <div className="flex space-x-3 bg-white rounded-full px-6 py-3 shadow-lg border border-[#7F8C8D]/10">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`rounded-full transition-all duration-300 ${
                      index === currentIndex 
                        ? 'bg-[#C0392B] w-10 h-3' 
                        : 'bg-[#7F8C8D]/30 hover:bg-[#7F8C8D]/60 w-3 h-3'
                    }`}
                    aria-label={`Go to testimonial ${index + 1}`}
                  />
                ))}
              </div>

              {/* Play/Pause Button */}
              <Button
                onClick={() => setIsPaused(!isPaused)}
                variant="outline"
                size="icon"
                className="w-14 h-14 rounded-full border-2 border-[#7F8C8D] text-[#7F8C8D] hover:bg-[#7F8C8D] hover:text-white transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl"
              >
                {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
              </Button>

              <Button
                onClick={nextTestimonial}
                variant="outline"
                size="icon"
                className="w-14 h-14 rounded-full border-2 border-[#C0392B] text-[#C0392B] hover:bg-[#C0392B] hover:text-white transition-all duration-300 hover:scale-110 shadow-lg hover:shadow-xl"
              >
                <ChevronRight className="w-6 h-6" />
              </Button>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;